import { useEffect, useState } from "react";
import { Crown, LogOut, Loader2, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { API, adminLogin, adminGetRegistrations, adminGetContacts, adminSaveContent, getContent } from "@/lib/api";
import { DEFAULT_CONTENT, LOGO } from "@/lib/site";

const inputCls = "input-lux !py-2 text-sm";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("hath_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("registrations");
  const [registrations, setRegistrations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState("");

  const loadData = async () => {
    try {
      const [regs, cons, cont] = await Promise.all([adminGetRegistrations(), adminGetContacts(), getContent()]);
      setRegistrations(regs);
      setContacts(cons);
      setContent({ stats: cont.stats, events: cont.events, gallery: cont.gallery, testimonials: cont.testimonials });
    } catch {
      toast.error("Session expired. Please log in again.");
      logout();
    }
  };

  useEffect(() => {
    if (token) loadData();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem("hath_admin_token", res.token);
      setToken(res.token);
      toast.success("Welcome back, Admin");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("hath_admin_token");
    setToken("");
  };

  const setField = (section, idx, key, val) => {
    setContent((c) => ({ ...c, [section]: c[section].map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  };

  const addItem = (section, blank) => setContent((c) => ({ ...c, [section]: [...c[section], blank] }));
  const removeItem = (section, idx) => setContent((c) => ({ ...c, [section]: c[section].filter((_, i) => i !== idx) }));

  const save = async (section) => {
    setSaving(section);
    try {
      await adminSaveContent({ [section]: content[section] });
      toast.success("Saved — live on the website now");
    } catch {
      toast.error("Save failed. Please log in again.");
    } finally {
      setSaving("");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-royal-950 flex items-center justify-center px-5 spotlight-top" data-testid="admin-login-page">
        <form onSubmit={login} className="glass-card gold-frame rounded-md p-10 w-full max-w-sm">
          <div className="text-center mb-8">
            <img src={LOGO} alt="HA Treasure House" className="h-16 w-16 rounded-full object-cover ring-1 ring-gold/60 mx-auto mb-4" />
            <h1 className="font-display text-2xl gold-text">Admin Panel</h1>
            <p className="text-xs text-muted mt-2 tracking-widest uppercase">HA Treasure House</p>
          </div>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className="input-lux mb-4" data-testid="admin-email-input" required />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="input-lux mb-6" data-testid="admin-password-input" required />
          <button type="submit" disabled={loading} className="w-full rounded-full bg-gold text-royal-950 py-3.5 text-sm font-semibold tracking-widest hover:bg-gold-light transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="admin-login-btn">
            {loading && <Loader2 size={16} className="animate-spin" />} LOG IN
          </button>
          <a href="/" className="block text-center mt-5 text-xs text-muted hover:text-gold transition-colors" data-testid="admin-back-to-site">← Back to website</a>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: "registrations", label: `Registrations (${registrations.length})` },
    { id: "messages", label: `Messages (${contacts.length})` },
    { id: "events", label: "Events" },
    { id: "gallery", label: "Gallery" },
    { id: "testimonials", label: "Testimonials" },
    { id: "stats", label: "Stats" },
  ];

  const SaveBtn = ({ section }) => (
    <button onClick={() => save(section)} disabled={saving === section} className="rounded-full bg-gold text-royal-950 px-8 py-2.5 text-xs font-bold tracking-widest hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] transition-all disabled:opacity-60 inline-flex items-center gap-2" data-testid={`save-${section}-btn`}>
      {saving === section && <Loader2 size={14} className="animate-spin" />} SAVE {section.toUpperCase()}
    </button>
  );

  const Row = ({ children, onDelete, testId }) => (
    <div className="glass-card rounded-md p-5 relative" data-testid={testId}>
      <button onClick={onDelete} className="absolute top-3 right-3 text-muted hover:text-fuchsia transition-colors" aria-label="Delete" data-testid={`${testId}-delete`}>
        <Trash2 size={16} />
      </button>
      <div className="grid sm:grid-cols-2 gap-3 pr-8">{children}</div>
    </div>
  );

  const In = ({ value, onChange, ph, testId, area }) =>
    area ? (
      <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph} className={`${inputCls} sm:col-span-2 resize-none`} data-testid={testId} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={ph} className={inputCls} data-testid={testId} />
    );

  const AddBtn = ({ section, blank, label }) => (
    <button onClick={() => addItem(section, blank)} className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-2.5 text-xs font-semibold tracking-widest text-gold hover:bg-gold/10 transition-colors" data-testid={`add-${section}-btn`}>
      <Plus size={14} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-royal-950 text-cream" data-testid="admin-dashboard">
      <header className="glass-panel sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="text-gold" size={22} />
            <span className="font-display text-lg gold-text font-semibold">HA Treasure House — Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs text-muted hover:text-gold transition-colors" data-testid="admin-view-site">View site</a>
            <button onClick={logout} className="inline-flex items-center gap-2 text-xs text-muted hover:text-fuchsia transition-colors" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`rounded-full px-5 py-2 text-xs font-semibold tracking-widest uppercase transition-all ${tab === t.id ? "bg-gold text-royal-950" : "border border-gold/30 text-muted hover:text-gold"}`} data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "registrations" && (
          <div className="space-y-3" data-testid="admin-registrations-list">
            {registrations.length === 0 && <p className="text-muted text-sm">No registrations yet.</p>}
            {registrations.map((r) => (
              <div key={r.id} className="glass-card rounded-md p-5 flex gap-5 items-start" data-testid={`admin-reg-${r.id}`}>
                {r.photo_url ? (
                  <img src={`${API}${r.photo_url}`} alt={r.full_name} className="h-20 w-20 object-cover rounded-md ring-1 ring-gold/40 shrink-0" />
                ) : (
                  <div className="h-20 w-20 rounded-md bg-royal-800 flex items-center justify-center text-muted text-xs shrink-0">No photo</div>
                )}
                <div className="text-sm flex-1">
                  <p className="font-display text-lg text-gold-light">{r.full_name} <span className="text-xs text-muted">({r.gender}, {r.age})</span></p>
                  <p className="text-cream/80">{r.category}{r.preferred_event ? ` — ${r.preferred_event}` : ""}</p>
                  <p className="text-muted text-xs mt-1">{r.mobile} • {r.email} • {r.city}{r.instagram ? ` • ${r.instagram}` : ""}</p>
                  {(r.talent || r.experience || r.message) && (
                    <p className="text-muted/80 text-xs mt-1">{[r.talent, r.experience].filter(Boolean).join(" • ")}{r.message ? ` — “${r.message}”` : ""}</p>
                  )}
                  <p className="text-muted/50 text-[10px] mt-2">{new Date(r.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages-list">
            {contacts.length === 0 && <p className="text-muted text-sm">No messages yet.</p>}
            {contacts.map((m) => (
              <div key={m.id} className="glass-card rounded-md p-5" data-testid={`admin-msg-${m.id}`}>
                <p className="font-display text-lg text-gold-light">{m.name}</p>
                <p className="text-muted text-xs">{m.email}{m.phone ? ` • ${m.phone}` : ""}</p>
                <p className="text-cream/85 text-sm mt-2">{m.message}</p>
                <p className="text-muted/50 text-[10px] mt-2">{new Date(m.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {content && tab === "events" && (
          <div className="space-y-4" data-testid="admin-events-editor">
            {content.events.map((ev, i) => (
              <Row key={i} testId={`event-row-${i}`} onDelete={() => removeItem("events", i)}>
                <In value={ev.name} onChange={(v) => setField("events", i, "name", v)} ph="Event name" testId={`event-name-${i}`} />
                <In value={ev.status} onChange={(v) => setField("events", i, "status", v)} ph="Status (e.g. Registrations Open / Coming Soon)" testId={`event-status-${i}`} />
                <In value={ev.city} onChange={(v) => setField("events", i, "city", v)} ph="City" testId={`event-city-${i}`} />
                <In value={ev.date} onChange={(v) => setField("events", i, "date", v)} ph="Date (e.g. November 2026)" testId={`event-date-${i}`} />
                <In value={ev.image} onChange={(v) => setField("events", i, "image", v)} ph="Image URL" testId={`event-image-${i}`} />
                <In area value={ev.description} onChange={(v) => setField("events", i, "description", v)} ph="Description" testId={`event-desc-${i}`} />
              </Row>
            ))}
            <div className="flex flex-wrap gap-4 pt-2">
              <AddBtn section="events" label="ADD EVENT" blank={{ id: `ev-${Date.now()}`, name: "", city: "", date: "", description: "", status: "Coming Soon", featured: false, image: "" }} />
              <SaveBtn section="events" />
            </div>
          </div>
        )}

        {content && tab === "gallery" && (
          <div className="space-y-4" data-testid="admin-gallery-editor">
            {content.gallery.map((g, i) => (
              <Row key={i} testId={`gallery-row-${i}`} onDelete={() => removeItem("gallery", i)}>
                <In value={g.url} onChange={(v) => setField("gallery", i, "url", v)} ph="Image URL" testId={`gallery-url-${i}`} />
                <In value={g.category} onChange={(v) => setField("gallery", i, "category", v)} ph="Category (Pageants, Winners…)" testId={`gallery-cat-${i}`} />
                <In value={g.caption} onChange={(v) => setField("gallery", i, "caption", v)} ph="Caption" testId={`gallery-caption-${i}`} />
                {g.url && <img src={g.url} alt="" className="h-14 w-14 object-cover rounded ring-1 ring-gold/30" />}
              </Row>
            ))}
            <div className="flex flex-wrap gap-4 pt-2">
              <AddBtn section="gallery" label="ADD PHOTO" blank={{ id: `g-${Date.now()}`, url: "", category: "Events", caption: "" }} />
              <SaveBtn section="gallery" />
            </div>
          </div>
        )}

        {content && tab === "testimonials" && (
          <div className="space-y-4" data-testid="admin-testimonials-editor">
            {content.testimonials.map((t, i) => (
              <Row key={i} testId={`testimonial-row-${i}`} onDelete={() => removeItem("testimonials", i)}>
                <In value={t.name} onChange={(v) => setField("testimonials", i, "name", v)} ph="Name" testId={`testimonial-name-${i}`} />
                <In value={t.title} onChange={(v) => setField("testimonials", i, "title", v)} ph="Title (e.g. Treasure Queen 2026)" testId={`testimonial-title-${i}`} />
                <In area value={t.quote} onChange={(v) => setField("testimonials", i, "quote", v)} ph="Quote" testId={`testimonial-quote-${i}`} />
              </Row>
            ))}
            <div className="flex flex-wrap gap-4 pt-2">
              <AddBtn section="testimonials" label="ADD TESTIMONIAL" blank={{ id: `t-${Date.now()}`, quote: "", name: "", title: "", placeholder: false }} />
              <SaveBtn section="testimonials" />
            </div>
          </div>
        )}

        {content && tab === "stats" && (
          <div className="space-y-4" data-testid="admin-stats-editor">
            {content.stats.map((s, i) => (
              <Row key={i} testId={`stat-row-${i}`} onDelete={() => removeItem("stats", i)}>
                <In value={String(s.value)} onChange={(v) => setField("stats", i, "value", Number(v) || 0)} ph="Value (number)" testId={`stat-value-input-${i}`} />
                <In value={s.suffix} onChange={(v) => setField("stats", i, "suffix", v)} ph="Suffix (e.g. +)" testId={`stat-suffix-${i}`} />
                <In value={s.label} onChange={(v) => setField("stats", i, "label", v)} ph="Label (e.g. Talents)" testId={`stat-label-${i}`} />
              </Row>
            ))}
            <div className="flex flex-wrap gap-4 pt-2">
              <SaveBtn section="stats" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
